import React, { useEffect, useState } from "react";
import {
  Flex,
  Tag,
  Typography,
  Button,
  Select,
  message,
  Spin,
} from "antd";
import {
  TeamOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { axiosInstance } from "@/utilities";
import { GroupSelect } from "./select-group";
import { useTranslation } from "react-i18next";

const { Text } = Typography;

interface ContactGroupCardProps {
  apiData: any[];
  contactId?: number;
  onDeleteGroup: (contactId: number, contactGroupRelationId: number) => void;
  updateRelations?: () => Promise<void>;
  updateContactRecord?: () => Promise<void>;
  loadingGroups?: boolean;
  contactRecord?: any;
  // "Agregar" ahora vive en el header del collapse (contactDetails). El card
  // solo reacciona: muestra el selector cuando addOpen y cierra vía onCloseAdd.
  addOpen?: boolean;
  onCloseAdd?: () => void;
}

export const ContactGroupCard: React.FC<ContactGroupCardProps> = ({
  apiData,
  contactId,
  onDeleteGroup,
  updateRelations,
  updateContactRecord,
  loadingGroups,
  contactRecord,
  addOpen,
  onCloseAdd,
}) => {
  const { t } = useTranslation();
  const [loadingSelect, setLoadingSelect] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [groupsData, setGroupsData] = useState<any[]>([]);

  const getRelations = async () => {
    setLoadingSelect(true);
    const storeCode = contactRecord?.storeCode || "";
    try {
      const res = await axiosInstance.get(
        `${API_BASE_URL}/contact-groups?filter.storeCode=${storeCode}`,
      );
      setGroupsData(res?.data?.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingSelect(false);
    }
  };

  useEffect(() => {
    if (addOpen) {
      getRelations();
    }
  }, [addOpen]);

  const handleSaveGroups = async () => {
    const payloads = selectedGroups.map((groupId) => ({
      contactId: contactId,
      contactGroupId: groupId,
      comment: "add in contact details",
    }));

    try {
      await Promise.all(
        payloads.map((payload) =>
          axiosInstance.post(
            `${API_BASE_URL}/contact-groups/relations`,
            payload,
          ),
        ),
      );
      message.success("Groups assigned!");
      updateRelations?.();
      updateContactRecord?.();
    } catch (e) {
      message.error("Error assigning groups");
    } finally {
      setSelectedGroups([]);
      onCloseAdd?.();
    }
  };

  // Grupos aplanados: ya no se discrimina por store (el contacto es per-store).
  const flattenGroups = (data: any[]) =>
    data
      .map((item) => {
        if (item.contactGroup) {
          return {
            contactGroupRelationId: item.contactGroupRelationId,
            contactGroupId: item.contactGroup.contactGroupId,
            name: item.contactGroup.name,
            contactId: item.contactId,
          };
        }
        if (item.name) {
          return {
            contactGroupRelationId: item.contactGroupId,
            contactGroupId: item.contactGroupId,
            name: item.name,
            contactId: item.contactId,
          };
        }
        return null;
      })
      .filter(Boolean) as {
        contactGroupRelationId: number;
        contactGroupId: number;
        name: string;
        contactId: number;
      }[];

  const groups = flattenGroups(apiData);

  const assignedGroupIds = contactRecord?.contactGroups?.map(
    (g: any) => g.contactGroupId,
  );

  return (
    <Spin spinning={loadingGroups}>
      <Flex vertical gap={12}>
        {addOpen && (
          <Flex vertical style={{ width: "100%" }} gap={12}>
            <GroupSelect
              value={selectedGroups}
              onChange={setSelectedGroups}
              loading={loadingSelect}
              options={groupsData}
              filter={assignedGroupIds}
            />
            <Flex justify="end" align="center">
              <Flex gap={8}>
                <Button size="small" onClick={() => onCloseAdd?.()}>
                  {t("buttons.cancel")}
                </Button>
                <Button size="small" type="primary" onClick={handleSaveGroups}>
                  {t("buttons.save")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        )}

        {groups.length > 0 ? (
          <Flex wrap="wrap" gap={8}>
            {groups.map((groupItem) => (
              <Tag
                key={groupItem.contactGroupRelationId}
                icon={<TeamOutlined />}
                closable={!contactRecord?.archived}
                closeIcon={<CloseCircleOutlined />}
                onClose={(e) => {
                  e.preventDefault();
                  onDeleteGroup(
                    groupItem.contactId,
                    groupItem.contactGroupRelationId,
                  );
                }}
              >
                {groupItem.name}
              </Tag>
            ))}
          </Flex>
        ) : (
          !addOpen && (
            <Text type="secondary" italic>
              {t("contacts.grups.noGroups")}
            </Text>
          )
        )}
      </Flex>
    </Spin>
  );
};
